<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useHeaderStore } from '@/stores/header';
import headerLogosSvgRaw from '@/assets/img/2026/header-logos.svg?raw';
import headerLogosAltRaw from '@/assets/img/2026/header-logos-alt.svg?raw';
import headerLogosStickyRaw from '@/assets/img/2026/header-logos-sticky.svg?raw';
import { MY_EVENT_TYPE } from '~~/shared/utilities/schedule';
const mobileNavShardPng = '/img/2026/mobile-nav-shard.png';
const mobileNavShard2xPng = '/img/2026/mobile-nav-shard@2x.png';

interface NavChild {
    label: string;
    href: string;
}

interface NavItem {
    label: string;
    href: string;
    children?: NavChild[];
    priority?: boolean;
}

const { loggedIn, user } = useUserSession();
const { $clientPosthog } = useNuxtApp();
const route = useRoute();
const headerStore = useHeaderStore();
const { menu, altLogo, pinkLogo, hasAnimated, sessionYear } = storeToRefs(headerStore);

const isActiveNav = (href) => {
    if (!href || href === '#') return false;
    // Exact path, or a section sub-path (e.g. /blog/* lights up Blog).
    if (route.path === href || route.path.startsWith(href + '/')) return true;
    // Session detail pages share /sessions/* for both current-year and archive
    // sessions, so the URL alone can't tell them apart. Disambiguate by the
    // viewed session's year (set by the session page in the header store):
    // current-year → Agenda, archive → that year's archive link (e.g.
    // /2025-sessions). Exactly one nav item lights up.
    if (route.path.startsWith('/sessions/')) {
        const year = sessionYear.value;
        if (year && year < 2026) return href === `/${year}-sessions`;
        return href === '/agenda';
    }
    return false;
};

const isItemActive = (item) => {
    // A nav item that owns a dropdown renders as a button, never a link, so it
    // can't be navigated to and its own URL carries no meaning — Agenda's is "/"
    // purely because it has no top-level page, which would otherwise light it up
    // on the homepage. Let its children decide. A dropdown that does have a
    // landing page should list it as an explicit subitem in Builder.
    if (item.children) {
        return item.children.some((child) => isActiveNav(child.href));
    }
    return isActiveNav(item.href);
};

const navItems = computed<NavItem[]>(() => {
    if (!menu.value?.length) return [];
    return menu.value
        .filter((item: any) => item.label !== 'Register')
        .map((item: any): NavItem => ({
            label: item.label,
            href: item.url || '#',
            priority: item.priority,
            children: item.subitems?.length
                ? item.subitems.map((sub: any): NavChild => ({
                    label: sub.label,
                    href: sub.url || '#',
                }))
                : undefined,
        }));
});

const registerItem = computed(() => {
    return menu.value?.find((item: any) => item.label === 'Register');
});

const mobileOpen = ref(false);
const mobileDialogRef = ref(null);
const mobileToggleRef = ref(null);
const logoRef = ref(null);
const navRef = ref(null);
const isSticky = ref(false);
const showSticky = ref(false);
const stickyNavActive = useState('stickyNavActive', () => false);
onMounted(() => {
    if (!hasAnimated.value) {
        setTimeout(() => { hasAnimated.value = true; }, 2000);
    }
});
const openDropdown = ref<number | string | null>(null);
const mobileExpanded = ref(null);
// The logged-in account links nest under an "Account" accordion on mobile
// (UNB-662017), so they get their own expand state separate from the numeric
// nav-item accordions above.
const accountExpanded = ref(false);
const mdBreakpoint = 1280;

let dropdownTimeout = null;

function showDropdown(index: number | string) {
    clearTimeout(dropdownTimeout);
    openDropdown.value = index;
}

function hideDropdown() {
    dropdownTimeout = setTimeout(() => {
        openDropdown.value = null;
    }, 150);
}

function onMobileKeydown(e) {
    if (e.key === 'Escape') {
        mobileOpen.value = false;
        return;
    }
    if (e.key !== 'Tab') return;

    const focusable = [
        mobileToggleRef.value,
        ...Array.from(mobileDialogRef.value?.querySelectorAll('a, button') || []),
    ].filter(Boolean);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
    }
}

watch(mobileOpen, (val) => {
    if (val) {
        mobileDialogRef.value?.show();
        document.documentElement.classList.add('locked');
        nextTick(() => mobileToggleRef.value?.focus());
        document.addEventListener('keydown', onMobileKeydown);
    } else {
        mobileDialogRef.value?.close();
        document.documentElement.classList.remove('locked');
        document.removeEventListener('keydown', onMobileKeydown);
        mobileExpanded.value = null;
    }
});

let lastScrollY = 0;
let navHeight = 0;

let scrollDelta = 0;
const SCROLL_THRESHOLD = 10;

function onScroll() {
    const scrollY = window.scrollY;
    const diff = scrollY - lastScrollY;
    scrollDelta += diff;

    if (scrollY > navHeight) {
        isSticky.value = true;
        if (scrollDelta < -SCROLL_THRESHOLD) {
            showSticky.value = true;
            scrollDelta = 0;
        } else if (scrollDelta > SCROLL_THRESHOLD) {
            showSticky.value = false;
            scrollDelta = 0;
        }
    } else {
        isSticky.value = false;
        showSticky.value = false;
        scrollDelta = 0;
    }

    lastScrollY = scrollY;
}

let logoCircles = null;
let logoGradient = null;

function onMouseMove(e) {
    if (!logoRef.value) return;
    const rect = logoRef.value.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    // Normalized offset: -1 to 1 range based on how far mouse is from logo center
    const dx = Math.max(-1, Math.min(1, (e.clientX - cx) / (window.innerWidth / 2)));
    const dy = Math.max(-1, Math.min(1, (e.clientY - cy) / (window.innerHeight / 2)));
    const angle = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI);

    if (logoGradient) {
        logoGradient.setAttribute('gradientTransform', `rotate(${angle}, 94, 11)`);
    }

    // Move the blurred circles (orange + fuchsia) to follow mouse direction
    if (logoCircles?.length === 2) {
        logoCircles[0].setAttribute('cx', 132 + dx * 80);
        logoCircles[0].setAttribute('cy', -54 + dy * 40);
        logoCircles[1].setAttribute('cx', 206 + dx * 60);
        logoCircles[1].setAttribute('cy', 43 + dy * 30);
    }
}

// Reactive twin of navHeight, used to size the mobile menu's logo scrim. Measured
// rather than hardcoded because the nav's own height changes with the breakpoint
// (the search/menu buttons go 32px -> 42px), and a fixed strip left uneven space
// above and below the logo (UNB).
const navBoxHeight = ref(0);
const measureNav = () => { navBoxHeight.value = navRef.value?.offsetHeight || 0; };

function onResize() {
    measureNav();
    if (window.innerWidth >= mdBreakpoint && mobileOpen.value) {
        mobileOpen.value = false;
    }
}

function trackNavClick(label: string, url: string) {
    $clientPosthog?.capture('header_nav_clicked', {
        nav_label: label,
        nav_url: url,
    });
}

async function handleLoginClick() {
    trackNavClick('Login', '/login');
    await navigateTo('/login');
}

async function handleLogout() {
    trackNavClick('Logout', '/logout');
    await navigateTo('/logout');
}

// Logged-in account menu (UNB-582). The icon used to log you straight out on a
// single click, with no way to reach My Schedule from the nav.
//
// My Schedule rides the same release gate as every other entry point to it
// (useScheduleControls → enableMyScheduleAfter), so the menu can't offer a link
// to a feature that hasn't launched. showMySchedule is client-only by design, so
// these items are naturally absent from the CDN-cached HTML and appear on mount.
//
// Braindate is deliberately NOT here yet: it's an August feature (confirmed by
// Nick on UNB-582) and there's no release gate for it, so it ships when there is
// one rather than as a dead link.
const { showMySchedule } = useScheduleControls();

const accountItems = computed(() => {
    const items = [];
    if (showMySchedule.value) {
        items.push({ label: 'My Schedule', href: '/agenda?tab=myschedule' });
        // My Events isn't a page — it's My Schedule filtered to the viewer's own
        // custom events via the My-Schedule-only "My Event" Session Type
        // (MY_EVENT_TYPE, see agenda-filters.vue).
        //
        // Pinned to the Details view. My Schedule defaults to Calendar, which is
        // day-scoped, so arriving here landed on one day — usually the Wednesday —
        // and someone whose events sit on other days saw an empty calendar and no
        // sense of what they had (Nick, UNB-582). Details is dayless, so `day`
        // stays `all` and every event shows.
        items.push({ label: 'My Events', href: `/agenda?tab=myschedule&view=list&session_types=${MY_EVENT_TYPE.id}` });
    }
    return items;
});

async function handleAccountNav(item: { label: string; href: string }) {
    openDropdown.value = null;
    mobileOpen.value = false;
    trackNavClick(item.label, item.href);
    await navigateTo(item.href);
}

// Clicking the icon opens the menu instead of logging out; logged out it still
// goes straight to /login, which is the whole interaction there.
//
// Opens rather than toggles, unlike the nav-item dropdowns. Those sit under a
// labelled pill you might click for other reasons; this icon exists only to open
// this menu, and hovering it has already opened it by the time a mouse user
// clicks — so a toggle would close the menu on the very click the ticket asks to
// open it with, and re-opening would need the pointer to leave and come back
// (mouseenter won't re-fire while it's already inside). Escape, moving away, and
// picking an item all close it.
function handleAccountClick() {
    if (!loggedIn.value) return handleLoginClick();
    showDropdown('account');
}

function onAccountKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && openDropdown.value === 'account') {
        openDropdown.value = null;
    }
}

onMounted(() => {
    const svg = logoRef.value?.querySelector('svg');
    if (svg) {
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    }

    logoGradient = logoRef.value?.querySelector('linearGradient');
    logoCircles = logoRef.value?.querySelectorAll('mask + g circle, g[mask] circle');
    if (!logoCircles?.length) {
        logoCircles = logoRef.value?.querySelectorAll('circle');
    }


    navHeight = navRef.value?.offsetHeight || 100;
    measureNav();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    window.addEventListener('mousemove', onMouseMove);
});

onUnmounted(() => {
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
});
</script>

<template>
    <nav ref="navRef"
        :class="[
            'block-2026-header w-full z-50 flex items-center justify-between',
            'px-[24px] xs:px-[var(--box-padding-lr-m)] pt-[20px] pb-[20px]',
            'lg:px-[40px] lg:pt-[32px] lg:pb-[42px]',
            isSticky
                ? 'fixed top-0 left-0 header-sticky'
                : 'absolute top-0 left-0',
            isSticky && showSticky ? 'header-visible' : '',
            isSticky && !showSticky ? 'header-hidden' : '',
            openDropdown !== null ? 'header-dropdown-open' : '',
            stickyNavActive ? 'header-pink-bg' : '',
            !hasAnimated ? 'initial-load' : '',
        ]"
        aria-label="Main navigation">

        <!-- Logo -->
        <NuxtLink to="/" style="animation-delay: 2s" :class="[
                'nav-drop-in shrink-0 relative z-[60] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-[4px]',
            ]"
            aria-label="UNBOUND 2027 home">
            <Transition name="logo-fade" mode="out-in">
                <div v-if="altLogo > 0 && (!isSticky || mobileOpen)" key="alt" class="header-logo" role="img" aria-label="UNBOUND 27 by HubSpot"
                    v-html="headerLogosAltRaw" />
                <div v-else-if="!isSticky || mobileOpen" key="full" ref="logoRef" :class="['header-logo', (mobileOpen || pinkLogo) ? 'header-logo-flat' : '']" role="img" aria-label="UNBOUND 27 by HubSpot"
                    v-html="headerLogosSvgRaw" />
                <div v-else key="sticky" class="flex items-center justify-center h-[32px] xs:h-[42px] px-[16px] rounded-[50px] bg-white shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]">
                    <div class="header-logo-sticky h-[14px]" role="img" aria-label="UNBOUND 27"
                        v-html="headerLogosStickyRaw" />
                </div>
            </Transition>
        </NuxtLink>

        <!-- Mobile controls -->
        <div class="xl:hidden relative z-[60] flex gap-[7px] xs:gap-[14px] items-center">
            <!-- Search button -->
            <button class="flex items-center justify-center w-[32px] h-[32px] xs:w-[42px] xs:h-[42px]
                rounded-full bg-white text-[var(--magenta)] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]
                border-none cursor-pointer transition-colors duration-200
                hover:bg-purple hover:text-white
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple"
                aria-label="Search"
                @click="$router.push({ query: { ...$route.query, modal: '/search/' } })">
                <svg aria-hidden="true" class="w-[33%] h-[33%]" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.056 9.056L12.5 12.5M0.5 5.389C0.5 6.686 1.015 7.929 1.932 8.846C2.849 9.763 4.092 10.278 5.389 10.278C6.686 10.278 7.929 9.763 8.846 8.846C9.763 7.929 10.278 6.686 10.278 5.389C10.278 4.092 9.763 2.849 8.846 1.932C7.929 1.015 6.686 0.5 5.389 0.5C4.092 0.5 2.849 1.015 1.932 1.932C1.015 2.849 0.5 4.092 0.5 5.389Z"
                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
            <!-- Menu toggle -->
            <button ref="mobileToggleRef" class="flex items-center justify-center w-[32px] h-[32px] xs:w-[42px] xs:h-[42px]
                rounded-full bg-white text-[var(--magenta)] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]
                border-none cursor-pointer transition-colors duration-200
                hover:bg-purple hover:text-white
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple"
                :aria-label="mobileOpen ? 'Close menu' : 'Open menu'"
                @click="mobileOpen = !mobileOpen"
                :aria-expanded="mobileOpen">
                <span aria-hidden="true" class="hamburger-bars">
                    <span :class="['hamburger-bar', mobileOpen ? 'bar-top-open' : '']" />
                    <span :class="['hamburger-bar', mobileOpen ? 'bar-mid-open' : '']" />
                    <span :class="['hamburger-bar', mobileOpen ? 'bar-bot-open' : '']" />
                </span>
            </button>
        </div>

        <!-- Desktop navigation -->
        <div class="hidden xl:flex gap-[12px] items-center">
            <div v-for="(item, i) in navItems" :key="item.label"
                class="relative"
                @mouseenter="item.children ? showDropdown(i) : null"
                @mouseleave="item.children ? hideDropdown() : null">
                <NuxtLink v-if="!item.children" :to="item.href" :style="{ animationDelay: `${2.1 + i * 0.07}s` }"
                    :class="[
                        'nav-drop-in nav-pill flex gap-[6px] items-center px-[16px] py-[8px]',
                        'rounded-full shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]',
                        'font-hubspot-serif text-[14px] leading-[1.4] tracking-[0.28px]',
                        'no-underline whitespace-nowrap transition-colors duration-200',
                        'hover:bg-purple hover:text-white',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple focus-visible:ring-offset-2',
                        isItemActive(item) ? 'bg-purple text-white' : 'bg-white text-[var(--magenta)]',
                    ]"
                    @click="trackNavClick(item.label, item.href)">
                    {{ item.label }}
                </NuxtLink>
                <button v-else :style="{ animationDelay: `${2.1 + i * 0.07}s` }"
                    :class="[
                        'nav-drop-in nav-pill flex gap-[6px] items-center px-[16px] py-[8px]',
                        'rounded-full shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]',
                        'font-hubspot-serif text-[14px] leading-[1.4] tracking-[0.28px]',
                        'whitespace-nowrap transition-colors duration-200 cursor-pointer border-none',
                        'hover:bg-purple hover:text-white',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple focus-visible:ring-offset-2',
                        (openDropdown === i || isItemActive(item)) ? 'bg-purple text-white' : 'bg-white text-[var(--magenta)]',
                    ]"
                    :aria-expanded="openDropdown === i"
                    @click="showDropdown(openDropdown === i ? null : i)">
                    {{ item.label }}
                    <svg aria-hidden="true"
                        class="shrink-0"
                        width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path v-if="openDropdown === i" d="M0.863 4L6.863 4" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                        <path v-else d="M0.863 4L3.863 4M3.863 4L6.863 4M3.863 4V1M3.863 4V7" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
                <!-- Dropdown -->
                <div v-if="item.children && openDropdown === i"
                    class="absolute top-full left-0 flex flex-col gap-[8px] pt-[12px]">
                    <NuxtLink v-for="child in item.children" :key="child.label"
                        :href="child.href"
                        :class="[
                            'flex items-center w-fit px-[14px] py-[6px]',
                            'rounded-full shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]',
                            'font-hubspot-serif text-[12px] leading-[1.4] tracking-[0.24px]',
                            'no-underline whitespace-nowrap transition-colors duration-200',
                            'hover:bg-purple hover:text-white',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple focus-visible:ring-offset-2',
                            isActiveNav(child.href) ? 'bg-purple text-white' : 'bg-white text-[var(--magenta)]',
                        ]">
                        {{ child.label }}
                    </NuxtLink>
                </div>
            </div>

            <button :style="{ animationDelay: `${2.1 + navItems.length * 0.07}s` }"
                class="nav-drop-in flex items-center justify-center w-[36px] h-[36px]
                rounded-full bg-white shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]
                border-none cursor-pointer transition-colors duration-200
                hover:bg-purple hover:text-white
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple focus-visible:ring-offset-2"
                aria-label="Search"
                @click="$router.push({ query: { ...$route.query, modal: '/search/' } })">
                <svg aria-hidden="true" width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.056 9.056L12.5 12.5M0.5 5.389C0.5 6.686 1.015 7.929 1.932 8.846C2.849 9.763 4.092 10.278 5.389 10.278C6.686 10.278 7.929 9.763 8.846 8.846C9.763 7.929 10.278 6.686 10.278 5.389C10.278 4.092 9.763 2.849 8.846 1.932C7.929 1.015 6.686 0.5 5.389 0.5C4.092 0.5 2.849 1.015 1.932 1.932C1.015 2.849 0.5 4.092 0.5 5.389Z"
                        stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>

            <div class="relative"
                @mouseenter="loggedIn ? showDropdown('account') : null"
                @mouseleave="loggedIn ? hideDropdown() : null"
                @keydown="onAccountKeydown">
            <button :style="{ animationDelay: `${2.1 + (navItems.length + 1) * 0.07}s` }"
                :class="[
                    'nav-drop-in flex items-center justify-center w-[36px] h-[36px]',
                    'rounded-full shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]',
                    'border-none cursor-pointer transition-colors duration-200',
                    'hover:bg-purple hover:text-white',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple focus-visible:ring-offset-2',
                    loggedIn ? 'bg-[var(--magenta)] text-white' : 'bg-white text-[var(--magenta)]',
                ]"
                :aria-label="loggedIn ? 'Account menu' : 'Login'"
                :aria-expanded="loggedIn ? openDropdown === 'account' : undefined"
                :aria-haspopup="loggedIn ? 'menu' : undefined"
                @click="handleAccountClick()">
                <!-- stroke-width 0.857 (= 12/14), not the default 1 (UNB-589). This icon
                     draws a 12-unit viewBox at 14px, so it scales by 1.167 and a 1-unit
                     stroke renders at 1.167px — 17% heavier than the search icon beside
                     it, which draws 13 units at 13px and lands on exactly 1px. Both are
                     authored at 1 unit; only the scale differs, which is why it looks
                     wrong while the markup looks fine. 12/14 cancels the scale so this
                     renders at 1px too. Re-derive it if the 14px size or the viewBox
                     ever change. -->
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="5.875" cy="3.125" r="2.625" stroke="currentColor" stroke-width="0.857" stroke-linejoin="round"/>
                    <path d="M5.9286 6C3.1671 6 0.9286 8.2386 0.9286 11H10.9286C10.9286 8.2386 8.69 6 5.9286 6Z" stroke="currentColor" stroke-width="0.857" stroke-linejoin="round"/>
                </svg>
            </button>
                <!-- Account dropdown. Same pill stack as the nav dropdowns above,
                     left-aligned under the account icon (UNB-582): the Register
                     button sits to this icon's right, so the narrow menu has room
                     to extend right without running off the edge (verified 1024+). -->
                <div v-if="loggedIn && openDropdown === 'account'"
                    class="absolute top-full left-0 flex flex-col items-start gap-[8px] pt-[12px] z-[2]">
                    <button v-for="item in accountItems" :key="item.label"
                        type="button"
                        :class="[
                            'flex items-center w-fit px-[14px] py-[6px]',
                            'rounded-full shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]',
                            'font-hubspot-serif text-[12px] leading-[1.4] tracking-[0.24px]',
                            'whitespace-nowrap transition-colors duration-200 cursor-pointer border-none',
                            'hover:bg-purple hover:text-white',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple focus-visible:ring-offset-2',
                            'bg-white text-[var(--magenta)]',
                        ]"
                        @click="handleAccountNav(item)">
                        {{ item.label }}
                    </button>
                    <button type="button"
                        :class="[
                            'flex items-center w-fit px-[14px] py-[6px]',
                            'rounded-full shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]',
                            'font-hubspot-serif text-[12px] leading-[1.4] tracking-[0.24px]',
                            'whitespace-nowrap transition-colors duration-200 cursor-pointer border-none',
                            'hover:bg-purple hover:text-white',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple focus-visible:ring-offset-2',
                            'bg-white text-[var(--magenta)]',
                        ]"
                        @click="openDropdown = null; handleLogout()">
                        Log Out
                    </button>
                </div>
            </div>

            <NuxtLink v-if="registerItem" :to="registerItem.url" :style="{ animationDelay: `${2.1 + (navItems.length + 2) * 0.07}s` }"
                :class="[
                    'nav-drop-in nav-pill flex items-center px-[16px] py-[8px]',
                    'rounded-full shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]',
                    'font-hubspot-serif text-[14px] leading-[1.4] tracking-[0.28px]',
                    'text-white no-underline whitespace-nowrap transition-colors duration-200',
                    'hover:bg-purple',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--magenta)]',
                    isActiveNav(registerItem.url) ? 'bg-purple' : 'bg-[var(--magenta)]',
                ]"
                @click="trackNavClick('Register', registerItem.url)">
                {{ registerItem.label }}
            </NuxtLink>
        </div>

        <!-- Mobile navigation overlay -->
        <dialog ref="mobileDialogRef"
            class="mobile-nav-dialog fixed inset-0 z-50 m-0 p-0 w-full h-svh max-w-none overflow-hidden
                bg-[var(--pink)] backdrop-blur-[10px] flex flex-col xl:hidden border-none"
            @cancel.prevent="mobileOpen = false">
            <!-- Frosted strip behind the logo. The panel below scrolls UNDER the
                 logo — its pt-[107px] reserves exactly this band — so without a
                 backdrop the nav pills slide up and collide with the logo mark
                 (UNB). It only bites once the list is long enough to scroll,
                 which is why QA saw it get worse with an accordion expanded.

                 Same blur as the desktop sticky nav (.header-sticky::before) and
                 no background of its own, which is what QA asked for. That rule
                 can't be reused here: it sits at z-index -1 inside the nav, i.e.
                 BEHIND this dialog, so it never covers the scrolling panel.
                 Layering is z-[1] panel < z-[2] strip < z-[60] logo, the last of
                 which lives on the nav above the dialog. -->
            <div
                class="mobile-nav-logo-scrim absolute inset-x-0 top-0 z-[2] pointer-events-none"
                :style="{ height: `${navBoxHeight || 72}px` }"
                aria-hidden="true"></div>

            <!-- flex-1 + min-h-0 so this scrolls WITHIN the fixed-height dialog
                 instead of growing to its content and pushing the bottom items
                 (Account / Login) off-screen with no way to reach them — the
                 Safari bug in UNB-662022 (a flex child needs min-h-0 to scroll,
                 and h-svh keeps the dialog inside the iOS toolbar). -->
            <div class="relative z-[1] flex-1 min-h-0 flex flex-col gap-[16px] px-[24px] pt-[107px] overflow-y-auto pb-[40px]">
                <template v-for="(item, i) in navItems" :key="item.label">
                    <!-- Parent with children: accordion -->
                    <div v-if="item.children"
                        :class="['mobile-nav-item flex flex-col gap-[14px] self-start items-start', mobileOpen ? 'mobile-nav-enter' : 'mobile-nav-exit']"
                        :style="{ '--item-index': i, paddingBottom: mobileExpanded === i ? '24px' : '0' }">
                        <button
                            :class="[
                                'flex gap-[14px] items-center px-[22px] py-[12px] rounded-[50px] border-none cursor-pointer',
                                'font-hubspot-serif text-[20px] leading-[1.3] tracking-[0.4px] whitespace-nowrap',
                                'transition-colors duration-200',
                                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple',
                                'hover:bg-purple hover:text-white',
                                mobileExpanded === i
                                    ? 'bg-[var(--magenta)] text-white'
                                    : 'bg-white text-[var(--magenta)] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]',
                            ]"
                            :aria-expanded="mobileExpanded === i"
                            @click="mobileExpanded = mobileExpanded === i ? null : i">
                            {{ item.label }}
                            <svg aria-hidden="true" class="shrink-0" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path v-if="mobileExpanded === i" d="M2 6H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                                <path v-else d="M6 2V10M2 6H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            </svg>
                        </button>
                        <template v-if="mobileExpanded === i">
                            <NuxtLink v-for="child in item.children" :key="child.label"
                                :href="child.href"
                                class="flex items-center px-[16px] py-[8px] rounded-[50px]
                                    bg-white shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]
                                    font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px]
                                    text-[var(--magenta)] no-underline whitespace-nowrap
                                    transition-colors duration-200
                                    hover:bg-purple hover:text-white
                                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple"
                                @click="mobileOpen = false">
                                {{ child.label }}
                            </NuxtLink>
                        </template>
                    </div>
                    <!-- Regular nav item pill -->
                    <NuxtLink v-else :to="item.href"
                        :class="['mobile-nav-item flex items-center px-[24px] py-[12px] rounded-[50px] bg-white shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)] font-hubspot-serif text-[20px] leading-[1.3] tracking-[0.4px] text-[var(--magenta)] no-underline whitespace-nowrap transition-colors duration-200 hover:bg-purple hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple self-start', mobileOpen ? 'mobile-nav-enter' : 'mobile-nav-exit']"
                        :style="{ '--item-index': i }"
                        @click="mobileOpen = false">
                        {{ item.label }}
                    </NuxtLink>
                </template>
                <!-- Register CTA -->
                <NuxtLink v-if="registerItem" :to="registerItem.url"
                    :class="['mobile-nav-item flex items-center px-6 py-3 rounded-[50px] bg-(--orange) font-hubspot-serif text-[20px] leading-[1.3] tracking-[0.4px] text-white no-underline whitespace-nowrap transition-colors duration-200 hover:bg-purple focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white self-start', mobileOpen ? 'mobile-nav-enter' : 'mobile-nav-exit']"
                    :style="{ '--item-index': navItems.length + (loggedIn ? 2 : 1) }"
                    @click="mobileOpen = false; trackNavClick('Register', registerItem.url)">
                    {{ registerItem.label }}
                </NuxtLink>
                <!-- Account (UNB-662017). Logged in, the account links nest under
                     an "Account" accordion — mirroring the desktop icon dropdown —
                     rather than sitting as flat top-level pills. Logged out, it's a
                     direct Login button. -->
                <div v-if="loggedIn"
                    :class="['mobile-nav-item flex flex-col gap-[14px] self-start items-start', mobileOpen ? 'mobile-nav-enter' : 'mobile-nav-exit']"
                    :style="{ '--item-index': navItems.length + 2, paddingBottom: accountExpanded ? '24px' : '0' }">
                    <button type="button"
                        :class="[
                            'flex gap-[14px] items-center px-[22px] py-[12px] rounded-[50px] border-none cursor-pointer',
                            'font-hubspot-serif text-[20px] leading-[1.3] tracking-[0.4px] whitespace-nowrap',
                            'transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple hover:bg-purple hover:text-white',
                            accountExpanded ? 'bg-[var(--magenta)] text-white' : 'bg-white text-[var(--magenta)] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)]',
                        ]"
                        :aria-expanded="accountExpanded"
                        @click="accountExpanded = !accountExpanded">
                        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" class="shrink-0">
                            <circle cx="5.875" cy="3.125" r="2.625" stroke="currentColor" stroke-linejoin="round"/>
                            <path d="M5.9286 6C3.1671 6 0.9286 8.2386 0.9286 11H10.9286C10.9286 8.2386 8.69 6 5.9286 6Z" stroke="currentColor" stroke-linejoin="round"/>
                        </svg>
                        Account
                        <svg aria-hidden="true" class="shrink-0" width="12" height="12" viewBox="0 0 12 12" fill="none">
                            <path v-if="accountExpanded" d="M2 6H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                            <path v-else d="M6 2V10M2 6H10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                        </svg>
                    </button>
                    <template v-if="accountExpanded">
                        <NuxtLink v-for="item in accountItems" :key="item.label"
                            :to="item.href"
                            class="flex items-center px-[16px] py-[8px] rounded-[50px] bg-white shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)] font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] text-[var(--magenta)] no-underline whitespace-nowrap transition-colors duration-200 hover:bg-purple hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple"
                            @click="mobileOpen = false; trackNavClick(item.label, item.href)">
                            {{ item.label }}
                        </NuxtLink>
                        <button type="button"
                            class="flex items-center px-[16px] py-[8px] rounded-[50px] bg-white shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)] font-hubspot-serif text-[17px] leading-[1.4] tracking-[0.34px] text-[var(--magenta)] whitespace-nowrap border-none cursor-pointer transition-colors duration-200 hover:bg-purple hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple"
                            @click="mobileOpen = false; handleLogout()">
                            Log Out
                        </button>
                    </template>
                </div>
                <!-- Logged out: direct Login -->
                <button v-else type="button"
                    :class="['mobile-nav-item flex items-center gap-2 px-6 py-3 rounded-[50px] bg-white text-[var(--magenta)] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.15)] font-hubspot-serif text-[20px] leading-[1.3] tracking-[0.4px] border-none cursor-pointer whitespace-nowrap transition-colors duration-200 hover:bg-purple hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple self-start', mobileOpen ? 'mobile-nav-enter' : 'mobile-nav-exit']"
                    :style="{ '--item-index': navItems.length + 2 }"
                    @click="mobileOpen = false; handleLoginClick()">
                    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="5.875" cy="3.125" r="2.625" stroke="currentColor" stroke-linejoin="round"/>
                        <path d="M5.9286 6C3.1671 6 0.9286 8.2386 0.9286 11H10.9286C10.9286 8.2386 8.69 6 5.9286 6Z" stroke="currentColor" stroke-linejoin="round"/>
                    </svg>
                    Login
                </button>
            </div>
            <!-- Decorative shard -->
            <div class="absolute bottom-0 left-0 w-full z-[0] pointer-events-none" aria-hidden="true">
                <img :src="mobileNavShardPng" :srcset="`${mobileNavShardPng} 1x, ${mobileNavShard2xPng} 2x`" alt="" class="w-full h-auto" loading="lazy" decoding="async" />
            </div>
        </dialog>
    </nav>
</template>

<style>
@keyframes nav-drop-in {
    from {
        opacity: 0;
        transform: translateY(-100%);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.initial-load .nav-drop-in {
    animation: nav-drop-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@media (prefers-reduced-motion: reduce) {
    .initial-load .nav-drop-in {
        animation: none;
        opacity: 1;
        transform: translateY(0);
    }

    .mobile-nav-item {
        animation: none;
        opacity: 1;
        transform: translateX(0);
    }

    .mobile-nav-dialog {
        transition: none;
    }

    .mobile-nav-dialog[open] {
        opacity: 1;
        transform: translateY(0);
    }
}

.header-sticky {
    transform: translateY(-100%);
}

.header-visible {
    transition: transform 0.3s ease;
}

.header-sticky::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    pointer-events: none;
    transition: bottom 0.25s ease;
}

.header-dropdown-open::before {
    bottom: -70px;
    transition: bottom 0.25s ease;
}

.header-pink-bg::before {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
    background: var(--pink);
}

/* Blur only, no background — matching .header-sticky::before above, which is the
   desktop sticky treatment QA pointed at. */
.mobile-nav-logo-scrim {
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
}

.header-hidden {
    transform: translateY(-100%);
}

.header-hidden::before {
    display: none;
}

.header-visible {
    transform: translateY(0);
}

.logo-fade-enter-active,
.logo-fade-leave-active {
    transition: opacity 0.2s ease;
}

.logo-fade-enter-from,
.logo-fade-leave-to {
    opacity: 0;
}

.mobile-nav-dialog {
    display: none;
    opacity: 0;
    transform: translateY(-100%);
    transition: opacity 0.35s ease, transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                display 0.35s allow-discrete;
}

.mobile-nav-dialog[open] {
    display: flex;
    opacity: 1;
    transform: translateY(0);
}

@starting-style {
    .mobile-nav-dialog[open] {
        opacity: 0;
        transform: translateY(-100%);
    }
}

.mobile-nav-dialog::backdrop {
    display: none;
}

@keyframes mobile-nav-slide-in {
    from {
        opacity: 0;
        transform: translateX(-20px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

@keyframes mobile-nav-slide-out {
    from {
        opacity: 1;
        transform: translateX(0);
    }
    to {
        opacity: 0;
        transform: translateX(-20px);
    }
}

.mobile-nav-item {
    opacity: 0;
    transform: translateX(-20px);
}

.mobile-nav-enter {
    animation: mobile-nav-slide-in 0.3s ease both;
    animation-delay: calc(var(--item-index) * 0.05s + 0.15s);
}

.mobile-nav-exit {
    animation: mobile-nav-slide-out 0.2s ease both;
    animation-delay: calc(var(--item-index) * 0.03s);
}

.hamburger-bars {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 38%;
    aspect-ratio: 16 / 12;
    position: relative;
}

.hamburger-bar {
    display: block;
    width: 100%;
    height: 1.5px;
    background: currentColor;
    border-radius: 1px;
    position: absolute;
    left: 0;
    transition: transform 0.3s ease, opacity 0.3s ease;
}

.hamburger-bar:nth-child(1) { top: 0; }
.hamburger-bar:nth-child(2) { top: 50%; transform: translateY(-50%); }
.hamburger-bar:nth-child(3) { bottom: 0; }

.bar-top-open {
    top: 50% !important;
    transform: translateY(-50%) rotate(45deg);
}

.bar-mid-open {
    opacity: 0;
    transform: translateY(-50%) scaleX(0);
}

.bar-bot-open {
    bottom: auto !important;
    top: 50%;
    transform: translateY(-50%) rotate(-45deg);
}

.header-logo {
    height: 16px;
    aspect-ratio: 200 / 16;
}

@media (width >= 430px) {
    .header-logo {
        height: 17px;
        aspect-ratio: 215 / 17;
    }
}

@media (min-width: 1024px) {
    .header-logo {
        height: 22px;
        aspect-ratio: 281 / 22;
    }
}

.header-logo-sticky {
    aspect-ratio: 121 / 14;
}

.header-logo-sticky svg {
    width: auto;
    height: 100%;
    display: block;
    overflow: visible;
}

.header-logo-flat svg * {
    fill: #46062b !important;
    stop-color: #46062b !important;
    transition: fill 0.3s ease, stop-color 0.3s ease;
}

.header-logo-flat svg [id="HubSpot Logo"] {
    fill: #804768 !important;
}

.header-logo svg {
    width: auto;
    height: 100%;
    display: block;
    overflow: visible;
}
</style>
